import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, Clock, Activity, AlertCircle, ShieldCheck, ArrowRight, XIcon } from 'lucide-react';
export function SupplementsGuide() {
  const [activeModal, setActiveModal] = useState(null);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <div className="min-h-screen bg-[#FAF9F6] text-stone-800 selection:bg-emerald-200 selection:text-emerald-900 pb-24" data-id="element-1">
      {/* Hero Section */}
      <header className="pt-24 pb-16 px-6 max-w-4xl mx-auto text-center" data-id="element-2">
        <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-3">
          <span className="text-orange-700 font-semibold tracking-widest uppercase text-xs md:text-sm mb-6 block" data-id="element-4">
            Salud & Bienestar
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-emerald-950 leading-tight mb-8 tracking-tight" data-id="element-5">
            ¿POR QUÉ NECESITAMOS SUPLEMENTOS HOY?
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 max-w-2xl mx-auto font-serif italic leading-relaxed" data-id="element-6">
            La guía práctica para entender por qué la alimentación moderna no
            siempre es suficiente.
          </p>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6" data-id="element-7">
        {/* Introduction */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="mb-16 md:mb-24" data-id="element-8">
          <div className="w-12 h-1 bg-emerald-800 mb-8 mx-auto md:mx-0" data-id="element-9"></div>
          <p className="text-lg md:text-xl leading-relaxed text-stone-700 mb-6" data-id="element-10">
            <span className="text-3xl md:text-4xl font-serif text-emerald-900 float-left mr-2 leading-none mt-1" data-id="element-11">
              D
            </span>
            urante años, hemos escuchado la misma frase: "Si comes bien, no
            necesitas suplementos". Aunque en teoría suena razonable, la
            realidad del siglo XXI es mucho más compleja.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-stone-700" data-id="element-12">
            Hoy no vivimos en el mismo mundo que nuestros abuelos. Los suelos
            están agotados, el estrés es una constante y nuestros ritmos de vida
            han cambiado drásticamente. Este bono fue creado para explicarte, de
            forma sencilla, por qué los suplementos bien elegidos se han
            convertido en una herramienta de apoyo indispensable para quienes
            quieren proteger su salud a largo plazo.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-12 md:space-y-16" data-id="element-13">
          {/* Section 1 */}
          <motion.section initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 relative mt-12" data-id="element-14">
            <div className="absolute -top-6 left-8 bg-emerald-100 text-emerald-800 p-3 rounded-xl shadow-sm" data-id="element-15">
              <Leaf size={28} strokeWidth={1.5} data-id="element-16" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6 mt-4" data-id="element-17">
              1. Los alimentos han cambiado (el suelo agotado)
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-6" data-id="element-18">
              Aunque compres la misma manzana que se compraba hace 50 años, el
              contenido mineral ya no es el mismo. La agricultura intensiva y el
              uso de fertilizantes químicos han agotado los nutrientes naturales
              del suelo.
            </p>
            <ul className="space-y-4 text-lg text-stone-700" data-id="element-19">
              <li className="flex items-start gap-3" data-id="element-20">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2.5 shrink-0" data-id="element-21"></div>
                <p data-id="element-22">
                  <strong data-id="element-23">Suelos pobres:</strong> Si el mineral no está en la
                  tierra, no llega a tu plato.
                </p>
              </li>
              <li className="flex items-start gap-3" data-id="element-24">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2.5 shrink-0" data-id="element-25"></div>
                <p data-id="element-26">
                  <strong data-id="element-27">Recolección temprana:</strong> Muchas frutas maduran
                  en camiones o cámaras, impidiendo que desarrollen su perfil
                  completo de vitaminas.
                </p>
              </li>
            </ul>
          </motion.section>

          {/* Section 2 */}
          <motion.section initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 relative mt-12" data-id="element-28">
            <div className="absolute -top-6 left-8 bg-emerald-100 text-emerald-800 p-3 rounded-xl shadow-sm" data-id="element-29">
              <Clock size={28} strokeWidth={1.5} data-id="element-30" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6 mt-4" data-id="element-31">
              2. La vida moderna: comer rápido y con estrés
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed" data-id="element-32">
              Incluso si tu dieta es "perfecta", el estrés crónico es un ladrón
              de nutrientes. Cuando el cuerpo está en modo de alerta, consume
              vitaminas del complejo B y Magnesio a una velocidad mucho mayor de
              lo normal. Además, comer de pie, distraído o con prisa altera
              nuestra digestión, impidiendo que absorbamos correctamente los
              nutrientes de esos buenos alimentos que compramos.
            </p>
          </motion.section>

          {/* Section 3 */}
          <motion.section initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 relative mt-12" data-id="element-33">
            <div className="absolute -top-6 left-8 bg-emerald-100 text-emerald-800 p-3 rounded-xl shadow-sm" data-id="element-34">
              <Activity size={28} strokeWidth={1.5} data-id="element-35" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6 mt-4" data-id="element-36">
              3. La edad y la absorción
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed" data-id="element-37">
              A medida que cruzamos la barrera de los 35 o 40 años, nuestra
              capacidad para producir ácido estomacal y enzimas digestivas
              disminuye. Esto significa que podemos estar comiendo nutrientes,
              pero nuestro cuerpo simplemente no los está "atrapando".
              Nutrientes críticos como la Vitamina B12 o el Hierro comienzan a
              ser más difíciles de absorber de manera natural.
            </p>
          </motion.section>

          {/* Section 4 */}
          <motion.section initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 relative mt-12" data-id="element-38">
            <div className="absolute -top-6 left-8 bg-emerald-100 text-emerald-800 p-3 rounded-xl shadow-sm" data-id="element-39">
              <AlertCircle size={28} strokeWidth={1.5} data-id="element-40" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6 mt-4" data-id="element-41">
              4. Deficiencias silenciosas (cuando la comida no alcanza)
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-6" data-id="element-42">
              Existen nutrientes que son extremadamente difíciles de obtener
              solo con comida en los niveles óptimos que el cuerpo necesita hoy:
            </p>
            <ul className="space-y-4 text-lg text-stone-700" data-id="element-43">
              <li className="flex items-start gap-3" data-id="element-44">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2.5 shrink-0" data-id="element-45"></div>
                <p data-id="element-46">
                  <strong data-id="element-47">Vitamina D:</strong> Casi imposible de obtener solo
                  con la dieta; requiere exposición solar que la mayoría de
                  nosotros no tiene por el trabajo en oficinas.
                </p>
              </li>
              <li className="flex items-start gap-3" data-id="element-48">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2.5 shrink-0" data-id="element-49"></div>
                <p data-id="element-50">
                  <strong data-id="element-51">Magnesio:</strong> Se estima que más del 70% de la
                  población tiene niveles insuficientes debido a su ausencia en
                  los suelos modernos.
                </p>
              </li>
              <li className="flex items-start gap-3" data-id="element-52">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600 mt-2.5 shrink-0" data-id="element-53"></div>
                <p data-id="element-54">
                  <strong data-id="element-55">Omega 3:</strong> A menos que consumas pescado azul de
                  alta calidad 3 o 4 veces por semana, es probable que tus
                  niveles sean bajos.
                </p>
              </li>
            </ul>
          </motion.section>

          {/* Section 5 */}
          <motion.section initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-stone-100 relative mt-12" data-id="element-56">
            <div className="absolute -top-6 left-8 bg-emerald-100 text-emerald-800 p-3 rounded-xl shadow-sm" data-id="element-57">
              <ShieldCheck size={28} strokeWidth={1.5} data-id="element-58" />
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6 mt-4" data-id="element-59">
              5. Apoyar, no sustituir
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed" data-id="element-60">
              Es fundamental entender que la suplementación apoya una buena
              alimentación, no la reemplaza. El objetivo no es "tomar de todo",
              sino cubrir esos huecos que la vida moderna nos deja. Como verás
              en tu Guía Inteligente, el secreto está en la estrategia: saber
              qué es útil para TI y cómo elegir calidad sobre cantidad.
            </p>
          </motion.section>
        </div>

        {/* Conclusion */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={fadeIn} className="mt-16 md:mt-24 bg-[#F2EFE9] rounded-2xl p-8 md:p-12 text-center border border-[#E5E0D8]" data-id="element-61">
          <h2 className="text-2xl md:text-3xl font-serif text-emerald-950 mb-6" data-id="element-62">
            Conclusión: La importancia de elegir bien
          </h2>
          <p className="text-lg text-stone-700 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-63">
            No se trata de consumir más, se trata de consumir mejor. Si decides
            añadir un suplemento a tu vida, asegúrate de que sea una inversión y
            no un gasto. Elige marcas transparentes, ingredientes de origen
            natural y fórmulas que tu cuerpo realmente reconozca.
          </p>

          <a href="https://suplementos.guias-salud.com" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-3 bg-orange-700 hover:bg-orange-800 text-white px-8 py-4 md:px-10 md:py-5 rounded-full text-lg font-medium transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1 w-full md:w-auto no-underline" data-id="element-64">
            <span data-id="element-65">
              👉 Click aquí si quieres más información sobre suplementos
            </span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-66" />
          </a>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="max-w-3xl mx-auto px-6 mt-20 pb-12" data-id="element-67">
        <div className="border-t border-stone-200 pt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-stone-500" data-id="element-68">
          <button onClick={() => setActiveModal('privacy')} className="hover:text-emerald-800 transition-colors underline underline-offset-2" data-id="element-69">
            Política de Privacidad
          </button>
          <span className="hidden sm:inline text-stone-300" data-id="element-70">·</span>
          <button onClick={() => setActiveModal('terms')} className="hover:text-emerald-800 transition-colors underline underline-offset-2" data-id="element-71">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-72">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-73">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()} data-id="element-74">
              <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 transition-colors" aria-label="Cerrar" data-id="element-75">
                <XIcon size={20} data-id="element-76" />
              </button>

              {activeModal === 'privacy' ? <>
                  <h3 className="text-2xl font-serif text-emerald-950 mb-6" data-id="element-77">
                    Política de Privacidad
                  </h3>
                  <div className="text-stone-600 leading-relaxed space-y-4 text-sm" data-id="element-78">
                    <p data-id="element-79">
                      Tu privacidad es importante para nosotros. Esta política
                      describe cómo recopilamos, usamos y protegemos tu
                      información personal.
                    </p>
                    <p data-id="element-80">
                      <strong data-id="element-81">Información que recopilamos:</strong> Podemos
                      recopilar información personal como nombre y correo
                      electrónico cuando te suscribes voluntariamente a nuestros
                      contenidos o guías.
                    </p>
                    <p data-id="element-82">
                      <strong data-id="element-83">Uso de la información:</strong> Utilizamos tu
                      información únicamente para enviarte contenido relevante
                      sobre salud y bienestar. No vendemos ni compartimos tus
                      datos con terceros.
                    </p>
                    <p data-id="element-84">
                      <strong data-id="element-85">Protección de datos:</strong> Implementamos
                      medidas de seguridad para proteger tu información personal
                      contra acceso no autorizado o divulgación.
                    </p>
                    <p data-id="element-86">
                      <strong data-id="element-87">Cookies:</strong> Este sitio puede utilizar
                      cookies para mejorar tu experiencia de navegación. Puedes
                      desactivarlas en la configuración de tu navegador.
                    </p>
                    <p data-id="element-88">
                      <strong data-id="element-89">Tus derechos:</strong> Puedes solicitar la
                      eliminación de tus datos en cualquier momento
                      contactándonos directamente.
                    </p>
                  </div>
                </> : <>
                  <h3 className="text-2xl font-serif text-emerald-950 mb-6" data-id="element-90">
                    Términos y Condiciones
                  </h3>
                  <div className="text-stone-600 leading-relaxed space-y-4 text-sm" data-id="element-91">
                    <p data-id="element-92">
                      Al acceder y utilizar este sitio web, aceptas los
                      siguientes términos y condiciones.
                    </p>
                    <p data-id="element-93">
                      <strong data-id="element-94">Contenido informativo:</strong> La información
                      proporcionada en esta guía es de carácter educativo y no
                      sustituye el consejo médico profesional. Consulta siempre
                      con un profesional de la salud antes de iniciar cualquier
                      suplementación.
                    </p>
                    <p data-id="element-95">
                      <strong data-id="element-96">Propiedad intelectual:</strong> Todo el contenido
                      de este sitio, incluyendo textos, diseños y gráficos, está
                      protegido por derechos de autor. No está permitida su
                      reproducción sin autorización previa.
                    </p>
                    <p data-id="element-97">
                      <strong data-id="element-98">Enlaces externos:</strong> Este sitio puede
                      contener enlaces a sitios de terceros. No nos hacemos
                      responsables del contenido o las prácticas de privacidad
                      de dichos sitios.
                    </p>
                    <p data-id="element-99">
                      <strong data-id="element-100">Limitación de responsabilidad:</strong> No
                      garantizamos resultados específicos derivados del uso de
                      suplementos mencionados en esta guía. Los resultados
                      pueden variar según cada persona.
                    </p>
                    <p data-id="element-101">
                      <strong data-id="element-102">Modificaciones:</strong> Nos reservamos el derecho
                      de modificar estos términos en cualquier momento. Las
                      modificaciones serán efectivas desde su publicación en
                      este sitio.
                    </p>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}